import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp, EQUIPMENT_CATEGORIES, SERVICE_CATEGORIES } from "@/contexts/AppContext";
import ListingCard from "@/components/ListingCard";
import ListingCardList from "@/components/ListingCardList";
import { lazy, Suspense } from "react";
const ListingsMap = lazy(() => import("@/components/ListingsMap"));
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Tractor, Wrench, MapPin, Loader2, Navigation, X, LayoutGrid, LayoutList, Map } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { getCurrentPosition, reverseGeocode, distanceKm } from "@/lib/geo";
import type { Listing } from "@/contexts/AppContext";

const STOPWORDS = new Set(["de", "da", "do", "das", "dos", "e", "em", "na", "no", "nas", "nos", "para", "por", "com", "um", "uma", "o", "a", "os", "as"]);
/** "Tratores John Deere em Chapecó" -> ["trator", "john", "deere", "chapeco"] */
function searchTerms(q: string): string[] {
  return q
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
    .replace(/[%_*,.()]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length >= 2 && !STOPWORDS.has(w))
    .map((w) => (w.length > 4 && w.endsWith("es") ? w.slice(0, -2) : w.length > 3 && w.endsWith("s") ? w.slice(0, -1) : w))
    .slice(0, 6);
}

const ALL_CATEGORIES = [...EQUIPMENT_CATEGORIES, ...SERVICE_CATEGORIES];
const PAGE_SIZE_OPTIONS = [20, 50];

function mapListing(row: any): Listing {
  return {
    id: row.id,
    title: row.title,
    category: row.category,
    description: row.description || "",
    price: row.price,
    priceUnit: row.price_unit,
    city: row.city,
    state: row.state,
    rating: 0,
    reviewCount: 0,
    images: row.images || [],
    availability: row.availability || [],
    phone: row.phone || "",
    whatsapp: row.whatsapp || "",
    email: row.email || "",
    ownerId: row.owner_id,
    ownerName: row.owner_name,
    featured: row.featured || false,
    status: row.status,
    views: row.views || 0,
    contactsRevealed: row.contacts_revealed || 0,
    createdAt: row.created_at,
    listingType: row.listing_type,
    latitude: row.latitude,
    longitude: row.longitude,
  };
}

export default function Index() {
  const { } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Lê filtros iniciais da URL (ex: /?type=servico)

  useEffect(() => {
    const type = searchParams.get("type");
    if (type === "servico" || type === "equipamento") {
      setActiveType(type);
      setCategory("all");
      setPage(1);
    } else if (!type) {
      // sem param, mantém "all"
    }
  }, [searchParams]);


  const [searchQuery, setSearchQuery] = useState("");
  const [searchActive, setSearchActive] = useState("");
  const [activeType, setActiveType] = useState("all");
  const [category, setCategory] = useState("all");
  const [dayFilter, setDayFilter] = useState("all");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [allListings, setAllListings] = useState<Listing[]>([]);
  const [locationLabel, setLocationLabel] = useState("");
  const [locating, setLocating] = useState(false);
  const [radiusKm, setRadiusKm] = useState(25);
  const [cityInput, setCityInput] = useState("");
  const [citySuggestions, setCitySuggestions] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<"grid" | "list" | "map">("grid");
  const [selectedMapId, setSelectedMapId] = useState<string>();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [listings, setListings] = useState<Listing[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const autocompleteTimer = useRef<any>(null);
  const totalPages = Math.ceil(totalCount / pageSize);
  const hasActiveFilters = activeType !== "all" || category !== "all" || dayFilter !== "all" || priceMin || priceMax || searchActive || userCoords;

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("type=recovery") || hash.includes("type=signup")) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) navigate("/redefinir-senha");
      });
    }
    const type = searchParams.get("type");
    if (type === "servico" || type === "equipamento") {
      setActiveType(type);
    }
  }, []);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    const isDistanceSort = sortBy === "distance" && userCoords;
    // Quando há filtro de localização, buscar um lote grande para filtrar por distância no cliente
    const needsClientFilter = isDistanceSort || !!userCoords;
    const fetchLimit = needsClientFilter ? 500 : pageSize;
    const fetchOffset = needsClientFilter ? 0 : (page - 1) * pageSize;

    let query = supabase
      .from("listings")
      .select("*", { count: "exact" })
      .eq("status", "active");

    if (activeType !== "all") query = query.eq("listing_type", activeType);
    if (category !== "all") query = query.eq("category", category);
    if (priceMin !== "") query = query.gte("price", Number(priceMin));
    if (priceMax !== "") query = query.lte("price", Number(priceMax));
    if (dayFilter !== "all") query = query.contains("availability", [dayFilter]);
    if (searchActive) {
      // cada palavra precisa aparecer (qualquer ordem), sem acento, em titulo/descricao/categoria/cidade/anunciante/loja
      for (const term of searchTerms(searchActive)) query = query.ilike("search_text", `%${term}%`);
    }

    if (!isDistanceSort) {
      query = query.order("featured", { ascending: false });
      if (sortBy === "price_asc") query = query.order("price", { ascending: true });
      else if (sortBy === "price_desc") query = query.order("price", { ascending: false });
      else if (sortBy === "rating") query = query.order("rating", { ascending: false });
      else query = query.order("bumped_at", { ascending: false });
    }

    query = query.range(fetchOffset, fetchOffset + fetchLimit - 1);

    const { data, error, count } = await query;

    if (error) {
      console.error("Erro ao buscar anuncios:", error);
      setLoading(false);
      return;
    }

    let mapped = (data || []).map(mapListing);
    setAllListings(mapped.filter((l) => l.latitude && l.longitude));
    if (dayFilter !== "all") {
      mapped = mapped.filter((l) => (l.availability || []).includes(dayFilter));
    }

    if (userCoords) {
      mapped = mapped.filter((l) => {
        if (!l.latitude || !l.longitude) return false;
        return distanceKm(userCoords.lat, userCoords.lng, l.latitude, l.longitude) <= radiusKm;
      });
    }

    if (isDistanceSort && userCoords) {
      mapped.sort((a, b) => {
        const dA = a.latitude && a.longitude
          ? distanceKm(userCoords.lat, userCoords.lng, a.latitude, a.longitude)
          : 9999;
        const dB = b.latitude && b.longitude
          ? distanceKm(userCoords.lat, userCoords.lng, b.latitude, b.longitude)
          : 9999;
        return dA - dB;
      });
      mapped.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
      setTotalCount(mapped.length);
      setListings(mapped.slice((page - 1) * pageSize, page * pageSize));
    } else if (needsClientFilter) {
      // Buscamos um lote grande e filtramos por distância no cliente: paginar aqui
      setTotalCount(mapped.length);
      setListings(mapped.slice((page - 1) * pageSize, page * pageSize));
    } else {
      setTotalCount(count ?? mapped.length);
      setListings(mapped);
    }

    setLoading(false);
  }, [page, pageSize, activeType, category, priceMin, priceMax, sortBy, searchActive, userCoords, radiusKm, dayFilter]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);
  usePullToRefresh(fetchListings);


  // Auto-detectar localização ao carregar
  useEffect(() => {
    // Tentar restaurar localização salva
    const saved = localStorage.getItem("userCoords");
    const savedLabel = localStorage.getItem("locationLabel");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setUserCoords(parsed);
        if (savedLabel) setLocationLabel(savedLabel);
      } catch {}
    }
    // Buscar localização atual
    getCurrentPosition().then((pos) => {
      if (pos) {
        setUserCoords(pos);
        localStorage.setItem("userCoords", JSON.stringify(pos));
        reverseGeocode(pos.lat, pos.lng).then((place) => {
          if (place) {
            const label = `${place.city}, ${place.state}`;
            setLocationLabel(label);
            localStorage.setItem("locationLabel", label);
          }
        });
      }
    }).catch(() => {});
  }, []);

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    setSearchActive(searchQuery);
    setPage(1);
  };

  const handleCityAutocomplete = (value: string) => {
    if (autocompleteTimer.current) clearTimeout(autocompleteTimer.current);
    if (value.length < 2) { setCitySuggestions([]); return; }
    autocompleteTimer.current = setTimeout(async () => {
      try {
        const res = await fetch("https://servicodados.ibge.gov.br/api/v1/localidades/municipios?orderBy=nome");
        const data = await res.json();
        const q = value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const matches = data
          .filter((m: any) => {
            const name = m.nome.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            return name.startsWith(q);
          })
          .slice(0, 6)
          .map((m: any) => ({
            label: `${m.nome}, ${m.microrregiao?.mesorregiao?.UF?.sigla || ""}`,
            nome: m.nome,
            uf: m.microrregiao?.mesorregiao?.UF?.sigla || "",
          }));
        setCitySuggestions(matches);
      } catch { setCitySuggestions([]); }
    }, 300);
  };

  const handleSelectCity = async (s: any) => {
    setCityInput(s.label);
    setLocationLabel(s.label);
    setCitySuggestions([]);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(s.nome + ", " + s.uf + ", Brasil")}&format=json&limit=1&countrycodes=br`,
        { headers: { "Accept-Language": "pt-BR" } }
      );
      const data = await res.json();
      if (data.length > 0) {
        setUserCoords({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
      }
    } catch {}
    setPage(1);
  };

  const handleGPS = async () => {
    setLocating(true);
    const pos = await getCurrentPosition();
    if (pos) {
      setUserCoords(pos);
      const place = await reverseGeocode(pos.lat, pos.lng);
      if (place) { setCityInput(place.city); setLocationLabel(`${place.city}, ${place.state}`); }
      else setLocationLabel("Sua localização atual");
    } else {
      alert("Não foi possível obter sua localização.");
    }
    setLocating(false);
    setPage(1);
  };

  const clearFilters = () => {
    setActiveType("all"); setCategory("all"); setPriceMin(""); setPriceMax("");
    setSortBy("recent"); setSearchQuery(""); setSearchActive("");
    setUserCoords(null); setLocationLabel(""); setCityInput(""); setPage(1);
  };

  const categoriesForType = activeType === "equipamento"
    ? EQUIPMENT_CATEGORIES
    : activeType === "servico"
    ? SERVICE_CATEGORIES
    : ALL_CATEGORIES;

  const getPageButtons = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | string)[] = [];
    if (page <= 4) {
      pages.push(1, 2, 3, 4, 5, "...", totalPages);
    } else if (page >= totalPages - 3) {
      pages.push(1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, "...", page - 1, page, page + 1, "...", totalPages);
    }
    return pages;
  };

  return (
    <div className="bg-background min-h-screen">

      <section className="bg-primary py-8 px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <h1 className="text-xl md:text-2xl font-heading font-bold text-primary-foreground mb-5">
            Equipamentos e serviços agrícolas perto de você
          </h1>
          <form onSubmit={handleSearch} className="flex gap-2 max-w-2xl mx-auto">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="O que você precisa?"
                className="bg-card text-card-foreground border-0 h-11 pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button type="submit" className="h-11 px-6 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shrink-0">
              <Search className="h-4 w-4 mr-2" /> Buscar
            </Button>
          </form>
        </div>
      </section>

      <section className="bg-card border-b border-border sticky top-16 z-40 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex gap-2 py-3">
            {[
              { value: "all", label: "Todos", icon: LayoutGrid },
              { value: "equipamento", label: "Equipamentos", icon: Tractor },
              { value: "servico", label: "Serviços", icon: Wrench },
            ].map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => { setActiveType(value); setCategory("all"); setPage(1); }}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeType === value
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-muted-foreground hover:bg-primary/10 hover:text-primary"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="grid grid-cols-2 md:grid-cols-12 gap-3">
            <div className="md:col-span-3">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Categoria</label>
              <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
                <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {categoriesForType.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Preço (R$)</label>
              <div className="flex gap-1 items-center">
                <Input placeholder="Min" type="number" min="0" className="h-9 text-sm"
                  value={priceMin} onChange={(e) => { setPriceMin(e.target.value); setPage(1); }} />
                <span className="text-muted-foreground text-xs shrink-0">ate</span>
                <Input placeholder="Max" type="number" min="0" className="h-9 text-sm"
                  value={priceMax} onChange={(e) => { setPriceMax(e.target.value); setPage(1); }} />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Dia disponível</label>
              <Select value={dayFilter} onValueChange={(v) => { setDayFilter(v); setPage(1); }}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Qualquer dia</SelectItem>
                  <SelectItem value="Seg">Segunda</SelectItem>
                  <SelectItem value="Ter">Terça</SelectItem>
                  <SelectItem value="Qua">Quarta</SelectItem>
                  <SelectItem value="Qui">Quinta</SelectItem>
                  <SelectItem value="Sex">Sexta</SelectItem>
                  <SelectItem value="Sab">Sábado</SelectItem>
                  <SelectItem value="Dom">Domingo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="relative md:col-span-3">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Localização</label>
              <div className="flex gap-1">
                <div className="relative flex-1">
                  <Input
                    placeholder="Sua cidade..."
                    className="h-9 text-sm"
                    value={cityInput}
                    onChange={(e) => { setCityInput(e.target.value); handleCityAutocomplete(e.target.value); }}
                    onKeyDown={(e) => { if (e.key === "Escape") setCitySuggestions([]); }}
                    autoComplete="off"
                  />
                  {citySuggestions.length > 0 && (
                    <div className="absolute top-10 left-0 right-0 bg-card border border-border rounded-md shadow-lg z-50 max-h-48 overflow-y-auto">
                      {citySuggestions.map((s, i) => (
                        <button key={i}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-secondary transition-colors flex items-center gap-2"
                          onClick={() => handleSelectCity(s)}>
                          <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={handleGPS} disabled={locating} title="Usar minha localização">
                  {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Navigation className="h-3.5 w-3.5" />}
                </Button>
              </div>
              {locationLabel && (
                <p className="text-xs text-primary mt-1 flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {locationLabel}
                </p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Raio de busca</label>
              <Select value={String(radiusKm)} onValueChange={(v) => { setRadiusKm(Number(v)); setPage(1); }} disabled={!userCoords}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["25", "50", "100", "200", "500"].map((r) => (
                    <SelectItem key={r} value={r}>{r} km</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="mt-3 flex items-center gap-1 text-xs text-destructive hover:text-destructive/80">
              <X className="h-3 w-3" /> Limpar filtros
            </button>
          )}
        </div>
      </section>

      <section className="py-4">
        <div className="container mx-auto px-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            {loading ? (
              <span className="flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Buscando...</span>
            ) : (
              <span>
                <strong className="text-foreground">{totalCount}</strong> anúncio{totalCount !== 1 ? "s" : ""} encontrado{totalCount !== 1 ? "s" : ""}
                {category !== "all" && ` em ${category}`}
                {locationLabel && ` - ${radiusKm}km de ${locationLabel}`}
              </span>
            )}
          </p>
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
            <button
              onClick={() => setViewMode(viewMode === "map" ? "grid" : "map")}
              className={`relative inline-flex h-8 w-20 items-center rounded-full transition-colors duration-300 focus:outline-none ${viewMode === "map" ? "bg-primary" : "bg-muted-foreground/30"}`}
              title={viewMode === "map" ? "Ver lista" : "Ver mapa"}
            >
              <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md transition-transform duration-300 ${viewMode === "map" ? "translate-x-[52px]" : "translate-x-1"}`}>
                <Map className={`h-3.5 w-3.5 ${viewMode === "map" ? "text-primary" : "text-muted-foreground"}`} />
              </span>
              <span className={`absolute text-xs font-semibold transition-all duration-300 ${viewMode === "map" ? "left-2 text-white" : "right-2 text-muted-foreground"}`}>
                Mapa
              </span>
            </button>

            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">Exibir:</span>
              <div className="flex border border-border rounded-md overflow-hidden">
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <button
                    key={size}
                    onClick={() => { setPageSize(size); setPage(1); }}
                    className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                      pageSize === size
                        ? "bg-primary text-primary-foreground"
                        : "bg-card text-muted-foreground hover:bg-secondary"
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex border border-border rounded-md overflow-hidden">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-1.5 transition-colors ${viewMode === "grid" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-secondary"}`}
                title="Grade"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-1.5 transition-colors ${viewMode === "list" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-secondary"}`}
                title="Lista"
              >
                <LayoutList className="h-4 w-4" />
              </button>
            </div>

          </div>
        </div>
      </section>

      <section className="pb-8">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              {cityInput ? (
                <div className="max-w-md mx-auto">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                    <MapPin className="h-6 w-6 text-amber-700" />
                  </div>
                  <span className="inline-block text-xs font-medium bg-amber-100 text-amber-800 px-3 py-1 rounded-full mb-3">Oportunidade</span>
                  <p className="font-semibold text-foreground mb-2">Nenhum anúncio em {cityInput} ainda</p>
                  <p className="text-sm mb-5">Seja o <strong>primeiro anunciante</strong> da sua cidade e apareça em destaque para todos os produtores da região.</p>
                  <a href="/criar-anuncio">
                    <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                      Publicar em {cityInput} →
                    </Button>
                  </a>
                </div>
              ) : (
                <div className="max-w-md mx-auto">
                  <div className="border-l-4 border-teal-500 bg-card rounded-r-lg p-5 text-left mb-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
                        <Tractor className="h-5 w-5 text-teal-700" />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground text-sm">AgroAluga está crescendo!</p>
                        <p className="text-xs text-muted-foreground">Seja um dos primeiros anunciantes da sua região</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">Ainda não há anúncios nessa busca — mas produtores próximos já estão se cadastrando.</p>
                    <div className="grid grid-cols-2 gap-2">
                      <a href="/criar-anuncio">
                        <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold w-full text-sm">Publicar grátis</Button>
                      </a>
                      <Button variant="outline" onClick={clearFilters} className="w-full text-sm gap-1">
                        <X className="h-3 w-3" /> Ver tudo
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              {viewMode === "map" ? (
                <Suspense fallback={<div className="h-[500px] bg-secondary rounded-lg animate-pulse" />}>
                  <div className="h-[500px] lg:h-[600px]">
                    <ListingsMap listings={allListings.length > 0 ? allListings : listings} selectedId={selectedMapId} onSelect={setSelectedMapId} userCoords={userCoords} radiusKm={radiusKm} />
                  </div>
                </Suspense>
              ) : viewMode === "grid" ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {listings.map((l) => <ListingCard key={l.id} listing={l} />)}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {listings.map((l) => <ListingCardList key={l.id} listing={l} />)}
                </div>
              )}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-1.5 mt-8 flex-wrap">
                  <Button
                    variant="outline" size="sm"
                    onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    disabled={page === 1}
                  >
                    &lsaquo;
                  </Button>
                  {getPageButtons().map((p, i) =>
                    p === "..." ? (
                      <span key={"e" + i} className="px-1 text-muted-foreground text-sm">...</span>
                    ) : (
                      <Button
                        key={p}
                        variant={page === p ? "default" : "outline"}
                        size="sm"
                        className={page === p ? "bg-primary text-primary-foreground min-w-8" : "min-w-8"}
                        onClick={() => { setPage(p as number); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      >
                        {p}
                      </Button>
                    )
                  )}
                  <Button
                    variant="outline" size="sm"
                    onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    disabled={page === totalPages}
                  >
                    &rsaquo;
                  </Button>
                </div>
              )}
              {totalPages > 1 && (
                <p className="text-center text-xs text-muted-foreground mt-3">
                  Página {page} de {totalPages} - {totalCount} anúncios
                </p>
              )}
            </>
          )}
        </div>
      </section>

      <section className="py-10 bg-primary/5 border-t border-border">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-xl font-heading font-bold mb-2">Tem equipamentos ou presta serviços agrícolas?</h2>
          <p className="text-muted-foreground text-sm mb-5">Anuncie gratuitamente e alcance produtores da sua região.</p>
          <Button onClick={() => navigate("/criar-anuncio")} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8">
            + Anunciar gratuitamente
          </Button>
        </div>
      </section>
    </div>
  );
}
